import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Col} from "reactstrap";
import SearchInput from "../../../../../../components/base/input/search_input";
import {Divider} from "@mui/material";
import ProductFeedbackCard from "../../../../../../components/app-specific/product-cards/product-feedback-card";
import TryAgain from "../../../../../../components/app-specific/try-again";
import {useParams} from "react-router-dom";

const ProductInformationFeedbacksView = ({loading: loadingProps}) => {
    const params = useParams()
    const [feedbacks, setFeedbacks] = useState([]);
    const [keywords, setKeywords] = useState('');
    const [loadingFeedbacks, setLoadingFeedbacks] = useState(true);
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingFeedbacks, [loadingProps, loadingFeedbacks])

    const filteredFeedbacks = useMemo(() => {
        const keywordsLower = keywords?.trim()?.toLowerCase() ?? '';
        if (!keywordsLower)
            return feedbacks;

        /**
         * Filters the given feedback based on the given keywords.
         *
         * * the following properties of the feedback is compared with the keyword:
         *      1. message
         *      2. user first name
         *      3. user last name
         *      4. customer name
         * @param feedback
         * @return {boolean}
         */
        const filterBasedOnKeywords = (feedback) => {
            const msgLower = feedback?.msg?.trim()?.toLowerCase() ?? '';
            const userFirstNameLower = feedback?.user?.firstName?.trim()?.toLowerCase() ?? '';
            const userLastNameLower = feedback?.user?.lastName?.trim()?.toLowerCase() ?? '';
            const customerNameLower = feedback?.user?.customer?.name?.trim()?.toLowerCase() ?? '';

            if (msgLower.includes(keywordsLower))
                return true;
            if (userFirstNameLower.includes(keywordsLower))
                return true;
            if (userLastNameLower.includes(keywordsLower))
                return true;
            return customerNameLower.includes(keywordsLower);
        };

        /**
         * Maps each child of the feedback and filters them based on the keywords, then filters the current
         * feedback (parent) based on the keyword
         *
         * * CAVEAT: if a child fits the keyword but its parent does not, it is not selected in the resulting list
         * @param feedback
         * @return {*&{children: (*|*[])}}
         */
        const mapFeedbacks = (feedback) => ({
            ...feedback,
            children: feedback?.children?.map(mapFeedbacks)?.filter(filterBasedOnKeywords) ?? [],
        });

        return feedbacks?.map(mapFeedbacks)?.filter(filterBasedOnKeywords);
    }, [keywords, feedbacks])

    /**
     * With each change in id of the url params:
     * - fetches the feedbacks' information of its respective product.
     */
    useEffect(() => {
        getProductFeedbacks().then()
    }, [params?.id])

    /**
     * Fetches the feedbacks' information of the current product from the server.
     * @return {Promise<void>}
     */
    const getProductFeedbacks = async () => {
        setLoadingFeedbacks(true);
        const response = await Api.getOperatorPartFeedbacks(params.id);
        if (!isMounted()) return;
        setLoadingFeedbacks(false);
        if (response?.resultFlag) {
            setFeedbacks(response?.data ?? [])
        }
    }

    /**
     * Toggles the visibility of this feedback of the user.
     * @param feedback
     * @param parent
     * @return {Promise<void>}
     */
    const toggleFeedbackVisibility = async (feedback, parent = null) => {
        const response = await Api.toggleFeedbackVisibility(feedback.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setFeedbacks(prevState => prevState.map(e =>
                e.id === feedback.id
                    ? {...e, isVisible: !e.isVisible}
                    : e.id === parent?.id
                        ? {
                            ...e,
                            children: e.children?.map(e => e.id === feedback.id
                                ? {...e, isVisible: !e.isVisible}
                                : e
                            )
                        }
                        : e
            ))
        }
    }

    /**
     * Remove the given feedback from the list of feedback of the current product.
     *
     * @param feedback
     * @param parent
     * @return {Promise<void>}
     */
    const removeFeedback = async (feedback, parent = null) => {
        const response = await Api.removeOperatorPartFeedback(feedback.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            if (parent) {
                setFeedbacks(prevState => prevState.map(e =>
                    e.id === parent?.id
                        ? {
                            ...e,
                            children: e.children?.filter(e => e.id !== feedback.id)
                        }
                        : e
                ))
            } else {
                setFeedbacks(prevState => prevState.filter(e => e.id !== feedback.id));
            }
        }
    }

    return (
        <>
            <div className={'product-feedbacks-view part-info-paper'}>
                <Col xs={12} className='px-3'>
                    <SearchInput
                        loading={loading}
                        autoSearch={true}
                        search={setKeywords}
                        initialValue={keywords}
                        label={'Search'}
                    />
                </Col>
                <Divider className='divider'/>
                <div className='w-100 my-2'/>
                <div className={'d-flex flex-column my-5 pt-4'}>
                    {
                        loading
                            ? <>
                                {
                                    Array(5).fill(null).map((_, e) => (
                                        <Col key={e} xs={12} className={'loading large mb-3'}>
                                            <div/>
                                        </Col>
                                    ))
                                }
                            </>
                            : !filteredFeedbacks?.length
                                ? <div className={'my-5'}>
                                    {
                                        !keywords?.trim()?.length
                                            ? <TryAgain
                                                text={'There are no feedbacks submitted for the current product.'}
                                                onClick={getProductFeedbacks}
                                                buttonText={'Try Again'}
                                            />
                                            : <TryAgain
                                                text={'There are no feedbacks that match the given filters.'}
                                                onClick={() => setKeywords('')}
                                                buttonText={'Remove filters'}
                                                showButton={true}
                                            />
                                    }
                                </div>
                                : <>
                                    {
                                        filteredFeedbacks?.map(feedback => (
                                            <Col key={feedback.id} xs={12}>
                                                <ProductFeedbackCard
                                                    feedback={feedback}
                                                    remove={removeFeedback}
                                                    toggleVisibility={toggleFeedbackVisibility}
                                                />
                                            </Col>
                                        ))
                                    }
                                </>
                    }
                </div>
            </div>
        </>
    )
}

export default ProductInformationFeedbacksView;
